import React, { useState, useEffect } from "react";
import Modal from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import {
  getUserAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
} from "../api/deliveryaddress";

const UserAddress = () => {
  const { logout } = useAuth();
  const user = JSON.parse(localStorage.getItem("user"));
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [addressForm, setAddressForm] = useState({
    name: "",
    phone_no: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zip_code: "",
  });
  const [editingAddressId, setEditingAddressId] = useState(null);

  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user) return;
      const token = localStorage.getItem("token");
      const res = await getUserAddresses(user._id, token);
      setAddresses(res);
      setLoading(false);
    };
    fetchAddresses();
  }, []);

  const openModal = (addressId = null) => {
    setEditingAddressId(addressId);

    if (addressId) {
      // Find the selected address from the current address list
      const selectedAddress = addresses.find((addr) => addr._id === addressId);
      if (selectedAddress) {
        setAddressForm({
          name: selectedAddress.name || "",
          phone_no: selectedAddress.phone_no || "",
          country: selectedAddress.country || "",
          state: selectedAddress.state || "",
          city: selectedAddress.city || "",
          address: selectedAddress.address || "",
          zip_code: selectedAddress.zip_code || "",
        });
      }
    } else {
      // If adding new, reset the form
      setAddressForm({
        name: "",
        phone_no: "",
        country: "",
        state: "",
        city: "",
        address: "",
        zip_code: "",
      });
    }

    setModalOpen(true);
  };

  const saveAddress = async () => {
    const errors = {};

    if (!/^[0-9]\d{9}$/.test(addressForm.phone_no))
      errors.phone_no = "Enter a valid 10-digit Indian mobile number";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const token = localStorage.getItem("token");
    if (editingAddressId) {
      await updateAddress(
        editingAddressId,
        { ...addressForm, userId: user._id },
        token
      );
    } else {
      await createAddress(
        { ...addressForm, userId: user._id, email: user.email },
        token
      );
    }
    const updated = await getUserAddresses(user._id, token);
    setAddresses(updated);
    setModalOpen(false);
  };

  const removeAddress = async (id) => {
    const token = localStorage.getItem("token");
    await deleteAddress(id, user._id, token);
    const updated = await getUserAddresses(user._id, token);
    setAddresses(updated);
  };

  const makeDefault = async (id) => {
    const token = localStorage.getItem("token");
    await setDefaultAddress(id, user._id, token);
    const updated = await getUserAddresses(user._id, token);
    setAddresses(updated);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-6 pt-20 sm:pt-24">
      <div className="max-w-5xl mx-auto bg-gray-900 rounded-2xl shadow-lg p-4 sm:p-8 border border-purple-700">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center mb-6 sm:mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
          Manage Addresses
        </h1>

        <div className="flex justify-center mb-6 sm:mb-8">
          <button
            onClick={() => openModal()}
            className="px-5 sm:px-8 py-2 sm:py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm sm:text-base md:text-lg font-semibold hover:scale-105 transition-all duration-200 shadow-lg"
          >
            + Add New Address
          </button>
        </div>

        {loading ? (
          <p className="text-center text-gray-400 text-sm sm:text-base">
            Loading addresses...
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {addresses.map((addr) => (
              <div
                key={addr._id}
                className={`bg-gray-800 rounded-2xl p-4 sm:p-5 border shadow-xl ${
                  addr.isDefault
                    ? "border-green-500 bg-opacity-90 bg-green-800"
                    : "border-purple-700"
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  {addr.isDefault && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-green-500/20 backdrop-blur-sm border border-green-400 text-green-300 text-xs rounded-full animate-pulse shadow-md">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Default
                    </span>
                  )}
                </div>

                <p className="font-bold text-base sm:text-lg md:text-xl mb-1 text-cyan-400 break-words">
                  {addr.name}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm md:text-base break-words leading-relaxed">
                  {addr.address}, {addr.city}, {addr.state}, {addr.country}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm md:text-base mt-1">
                  {addr.phone_no}
                </p>

                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => openModal(addr._id)}
                    className="px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 hover:scale-105 transition-all text-white text-xs sm:text-sm md:text-base shadow-md"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => removeAddress(addr._id)}
                    className="px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-red-500 to-red-700 hover:scale-105 transition-all text-white text-xs sm:text-sm md:text-base shadow-md"
                  >
                    Delete
                  </button>

                  {!addr.isDefault && (
                    <button
                      onClick={() => makeDefault(addr._id)}
                      className="px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-green-700 hover:scale-105 transition-all text-white text-xs sm:text-sm md:text-base shadow-md"
                    >
                      Set Default
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingAddressId ? "Edit Address" : "Add Address"}
      >
        <form className="space-y-4">
          {Object.entries(addressForm).map(([key, value]) => (
            <div key={key} className="relative">
              <label className="block text-xs sm:text-sm md:text-base mb-1 text-purple-300 font-semibold">
                {key.replace(/_/g, " ").toUpperCase()}
              </label>
              <input
                name={key}
                maxLength={key === "phone_no" ? 10 : undefined}
                value={value}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, [key]: e.target.value })
                }
                className={`w-full p-3 rounded-lg bg-gray-800 border ${
                  formErrors[key] ? "border-red-500" : "border-purple-700"
                } text-white focus:ring-2 ${
                  formErrors[key] ? "focus:ring-red-400" : "focus:ring-cyan-500"
                } text-sm sm:text-base`}
              />
              {formErrors[key] && (
                <p className="text-red-400 font-semibold text-xs mt-1">{formErrors[key]}</p>
              )}
            </div>
          ))}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={saveAddress}
              className="px-5 sm:px-6 py-2 sm:py-3 rounded-xl bg-gradient-to-r from-purple-800 to-gray-700 text-white font-semibold hover:scale-105 transition-all shadow-md text-sm sm:text-base"
            >
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserAddress;
